import { create } from 'zustand';

// Callback para refrescar datos - será registrado por DashboardPage
type RefreshCallback = () => void;
let refreshCallback: RefreshCallback | null = null;

export function registerRefreshCallback(callback: RefreshCallback) {
  refreshCallback = callback;
}

export function unregisterRefreshCallback() {
  refreshCallback = null;
}

export function triggerRefresh() {
  if (refreshCallback) {
    refreshCallback();
  }
}

export interface FileItem {
  id: string;
  folderId: string | null;
  name: string;
  originalName: string;
  mimeType: string;
  size: number;
  thumbnailKey?: string | null;
  isStarred: boolean;
  isTrashed: boolean;
  trashedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FolderItem {
  id: string;
  parentId: string | null;
  clientId?: string | null;
  name: string;
  path: string;
  color?: string | null;
  fileCount?: number;
  createdAt: string;
  updatedAt: string;
}

type ViewMode = 'grid' | 'list';
type SortBy = 'name' | 'size' | 'createdAt' | 'updatedAt';
type SortOrder = 'asc' | 'desc';

interface FileState {
  files: FileItem[];
  folders: FolderItem[];
  selectedIds: string[];
  currentFolderId: string | null;
  currentProjectId: string | null;
  currentClientId: string | null;
  viewMode: ViewMode;
  sortBy: SortBy;
  sortOrder: SortOrder;
  isLoading: boolean;
  searchQuery: string;
  breadcrumb: { id: string; name: string }[];

  // Actions
  setFiles: (files: FileItem[]) => void;
  setFolders: (folders: FolderItem[]) => void;
  addFile: (file: FileItem) => void;
  addFolder: (folder: FolderItem) => void;
  updateFile: (id: string, updates: Partial<FileItem>) => void;
  updateFolder: (id: string, updates: Partial<FolderItem>) => void;
  removeFile: (id: string) => void;
  removeFolder: (id: string) => void;
  setSelectedIds: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setCurrentFolderId: (id: string | null) => void;
  setCurrentProjectId: (id: string | null) => void;
  setCurrentClientId: (id: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setSortBy: (sortBy: SortBy) => void;
  setSortOrder: (order: SortOrder) => void;
  setIsLoading: (loading: boolean) => void;
  setSearchQuery: (query: string) => void;
  setBreadcrumb: (breadcrumb: { id: string; name: string }[]) => void;
}

export const useFileStore = create<FileState>((set) => ({
  files: [],
  folders: [],
  selectedIds: [],
  currentFolderId: null,
  currentProjectId: null,
  currentClientId: null,
  viewMode: 'grid',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  isLoading: false,
  searchQuery: '',
  breadcrumb: [],

  setFiles: (files) => set({ files }),
  setFolders: (folders) => set({ folders }),

  addFile: (file) => set((state) => ({ files: [file, ...state.files] })),

  addFolder: (folder) =>
    set((state) => ({ folders: [...state.folders, folder] })),

  updateFile: (id, updates) =>
    set((state) => ({
      files: state.files.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    })),

  updateFolder: (id, updates) =>
    set((state) => ({
      folders: state.folders.map((f) =>
        f.id === id ? { ...f, ...updates } : f
      ),
    })),

  removeFile: (id) =>
    set((state) => ({
      files: state.files.filter((f) => f.id !== id),
      selectedIds: state.selectedIds.filter((sid) => sid !== id),
    })),

  removeFolder: (id) =>
    set((state) => ({
      folders: state.folders.filter((f) => f.id !== id),
      selectedIds: state.selectedIds.filter((sid) => sid !== id),
    })),

  setSelectedIds: (ids) => set({ selectedIds: ids }),

  toggleSelection: (id) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(id)
        ? state.selectedIds.filter((sid) => sid !== id)
        : [...state.selectedIds, id],
    })),

  selectAll: () =>
    set((state) => ({
      selectedIds: [
        ...state.files.map((f) => f.id),
        ...state.folders.map((f) => f.id),
      ],
    })),

  clearSelection: () => set({ selectedIds: [] }),

  setCurrentFolderId: (id) => set({ currentFolderId: id, selectedIds: [] }),

  setCurrentProjectId: (id) => set({ currentProjectId: id }),

  setCurrentClientId: (id) => set({ currentClientId: id }),

  setViewMode: (mode) => set({ viewMode: mode }),

  setSortBy: (sortBy) => set({ sortBy }),

  setSortOrder: (order) => set({ sortOrder: order }),

  setIsLoading: (loading) => set({ isLoading: loading }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setBreadcrumb: (breadcrumb) => set({ breadcrumb }),
}));
